import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { debounce } from 'lodash';

import { AppDispatch } from 'app/store';

import { getModuleSortConfig } from 'components/TableControls/sortOptions';

import { selectFilters } from 'features/filters/filtersSelectors';
import {
  setClearFilters,
  setFilters,
  updateFilterField,
  updateMultipleFilters
} from 'features/filters/filtersSlice';
import {
  selectModuleFilters,
  selectModuleFiltersLoading
} from 'features/moduleConfigs/moduleConfigsSelectors';
import {
  initializeModuleConfig,
  setActiveModule,
  setFiltersLoading,
  updateModuleFilters
} from 'features/moduleConfigs/moduleConfigsSlice';

import {
  cleanFilters,
  hasSignificantFilterChanges,
  normalizePageSizeForModule
} from 'utils/filtersUtils';

import { Filters } from 'types/filters';

import { AppModules, FilterFields, SidebarContentTypes } from 'constants/enums';
import { MODULE_DEFAULT_FACET_FIELDS } from 'constants/facets';
import {
  DEFAULT_FILTERS,
  FILTERS_DEBOUNCE_TIMEOUT_MS
} from 'constants/uiSettings';

interface UseFiltersReturn {
  filters: Filters;
  updateFilter: <T extends keyof Filters>(field: T, value: Filters[T]) => void;
  updateFilters: (updates: Partial<Filters>) => void;
  isLoading: boolean;
  resetFilters: () => void;
  isFilterLoadedAndReady: boolean;
}

interface UseFiltersOptions {
  debounceFilters?: number;
  autoSave?: boolean;
  autoClearOnUnmount?: boolean;
  facetFieldsByCountryConfig?: {
    triggerCountryCodes: string[];
    additionalFacetFields: string[];
  };
}

// Check if filters are ready to be applied
const isFiltersReady = (filters: Partial<Filters>): boolean => {
  return (
    filters[FilterFields.Page] !== null &&
    filters[FilterFields.PageSize] !== undefined
  );
};

// Map AppModules to SidebarContentTypes for sort config
const MODULE_TO_SIDEBAR_TYPE: Partial<Record<AppModules, SidebarContentTypes>> =
  {
    [AppModules.Jobs]: SidebarContentTypes.JobsFilters,
    [AppModules.Jobseekers]: SidebarContentTypes.JobseekersFilters,
    [AppModules.Applications]: SidebarContentTypes.ApplicationsFilters
  };

const toArray = <T>(value: T | T[] | undefined): T[] => {
  if (!value) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
};

const areSameStringArrays = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const useFilters = (
  module: AppModules,
  options: UseFiltersOptions = {}
): UseFiltersReturn => {
  const {
    debounceFilters = FILTERS_DEBOUNCE_TIMEOUT_MS,
    autoSave = true,
    autoClearOnUnmount = false,
    facetFieldsByCountryConfig
  } = options;

  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const currentFilters = useSelector(selectFilters);
  const savedFilters = useSelector(selectModuleFilters(module));
  const isLoading = useSelector(selectModuleFiltersLoading(module));

  const prevFiltersRef = useRef<Partial<Filters>>(currentFilters);
  const initializationDoneRef = useRef(false);

  // Readiness should depend only on initialization + valid filter payload.
  // Autosave loading is a separate UI concern and must not trigger data refetches.
  const isFilterLoadedAndReady = useMemo(() => {
    return initializationDoneRef.current && isFiltersReady(currentFilters);
  }, [currentFilters]);

  // Filters initialization
  useEffect(() => {
    if (initializationDoneRef.current) return;

    dispatch(initializeModuleConfig(module));
    dispatch(setActiveModule(module));

    // Set flag to avoid duplicate initialization
    initializationDoneRef.current = true;

    const filtersToRestore: Partial<Filters> = savedFilters
      ? { ...DEFAULT_FILTERS, ...savedFilters }
      : { ...DEFAULT_FILTERS };

    filtersToRestore.pageSize = normalizePageSizeForModule(
      module,
      filtersToRestore.pageSize
    );

    // Restore page from URL (priority above savedFilters)
    const pageFromUrl = searchParams.get('page');
    if (pageFromUrl) {
      const pageNumber = parseInt(pageFromUrl, 10);
      if (!isNaN(pageNumber) && pageNumber > 0) {
        filtersToRestore.page = pageNumber - 1; // Конвертируем в 0-based
      }
    } else {
      // If page is not in URL - always start with the first page
      // Ignore savedFilters.page if page is not in URL
      filtersToRestore.page = 0;
    }

    const sidebarType = MODULE_TO_SIDEBAR_TYPE[module];
    if (sidebarType) {
      const MODULE_SORT_CONFIG = getModuleSortConfig(t);
      let sortConfig;
      switch (sidebarType) {
        case SidebarContentTypes.JobsFilters:
          sortConfig = MODULE_SORT_CONFIG.jobs;
          break;
        case SidebarContentTypes.JobseekersFilters:
          sortConfig = MODULE_SORT_CONFIG.jobseekers;
          break;
        case SidebarContentTypes.ApplicationsFilters:
          sortConfig = MODULE_SORT_CONFIG.applications;
          break;
      }

      if (sortConfig?.defaultOption) {
        const options = Object.values(sortConfig.options);
        const matchedOption = options.find((option) => {
          if (option.order == null) {
            return option.field === filtersToRestore.sortField;
          }
          return (
            option.field === filtersToRestore.sortField &&
            option.order === filtersToRestore.order
          );
        });

        // Apply module default when sort came from another module.
        if (!matchedOption) {
          const defaultSort = sortConfig.options[sortConfig.defaultOption];
          if (defaultSort) {
            filtersToRestore.sortField = defaultSort.field;
            filtersToRestore.order = defaultSort.order ?? null;
          }
        } else if (
          matchedOption.order == null &&
          filtersToRestore.order != null
        ) {
          // Normalize "orderless" sort options to avoid leaking previous order.
          filtersToRestore.order = null;
        }
      }
    }

    const moduleDefaultFacetFields = MODULE_DEFAULT_FACET_FIELDS[module];
    if (moduleDefaultFacetFields && !filtersToRestore.facetFields) {
      filtersToRestore.facetFields = moduleDefaultFacetFields;
    }

    dispatch(setFilters(filtersToRestore));
    prevFiltersRef.current = filtersToRestore;
  }, [dispatch, module, savedFilters, t]);

  // Debounced function to save filters
  const debouncedSaveFilters = useCallback(
    debounce((filters: Partial<Filters>) => {
      if (!autoSave) return;

      const cleanedFilters = cleanFilters(filters);

      if (Object.keys(cleanedFilters).length > 0) {
        dispatch(
          updateModuleFilters({
            module,
            filters: cleanedFilters
          })
        );
      }

      dispatch(setFiltersLoading({ module, isLoading: false }));
    }, debounceFilters),
    [dispatch, module, autoSave, debounceFilters]
  );

  // Tracking changes of filters for auto-saving
  useEffect(() => {
    // Skip initializations and cases when initialization is not completed
    if (!initializationDoneRef.current) return;

    const hasChanges = hasSignificantFilterChanges(
      prevFiltersRef.current,
      currentFilters
    );

    if (hasChanges) {
      if (autoSave) {
        dispatch(setFiltersLoading({ module, isLoading: true }));
        debouncedSaveFilters(currentFilters);
      }
      prevFiltersRef.current = currentFilters;
    }
  }, [currentFilters, debouncedSaveFilters, module, autoSave, dispatch]);

  useEffect(() => {
    if (!facetFieldsByCountryConfig) return;
    if (!initializationDoneRef.current) return;

    const { triggerCountryCodes, additionalFacetFields } =
      facetFieldsByCountryConfig;

    const currentFacetFields = currentFilters.facetFields ?? [];
    const countryCodes = toArray(currentFilters.country);
    const shouldHaveAdditionalFacetFields = countryCodes.some((code) =>
      triggerCountryCodes.includes(code)
    );

    const nextFacetFields = shouldHaveAdditionalFacetFields
      ? [
          ...currentFacetFields,
          ...additionalFacetFields.filter(
            (field) => !currentFacetFields.includes(field)
          )
        ]
      : currentFacetFields.filter(
          (field) => !additionalFacetFields.includes(field)
        );

    if (areSameStringArrays(nextFacetFields, currentFacetFields)) return;

    dispatch(
      updateMultipleFilters({
        facetFields: nextFacetFields.length ? nextFacetFields : undefined
      })
    );
  }, [
    currentFilters.country,
    currentFilters.facetFields,
    dispatch,
    facetFieldsByCountryConfig
  ]);

  const updateFilter = useCallback(
    <T extends keyof Filters>(field: T, value: Filters[T]) => {
      dispatch(updateFilterField({ field, value }));
    },
    [dispatch]
  );

  const updateFilters = useCallback(
    (updates: Partial<Filters>) => {
      dispatch(updateMultipleFilters(updates));
    },
    [dispatch]
  );

  const resetFilters = useCallback(() => {
    dispatch(setClearFilters());
  }, [dispatch]);

  // Clean up debounced function and filters on unmount
  useEffect(() => {
    return () => {
      debouncedSaveFilters.cancel();
      if (autoClearOnUnmount) {
        dispatch(setClearFilters());
        dispatch(updateModuleFilters({ module, filters: {} }));
      }
    };
  }, [debouncedSaveFilters, dispatch, autoClearOnUnmount, module]);

  return {
    filters: currentFilters,
    updateFilter,
    updateFilters,
    isLoading,
    resetFilters,
    isFilterLoadedAndReady
  };
};
